import React, { useState, useEffect, useRef, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toastify } from "../../../../../utils/toast";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import {
  getFacilityInvoices,
  voidInvoice,
  getFacilityUnits,
  getFacilityInvoiceBalances,
  getContracts,
} from "../../../../../utils/urls";
import { getItem } from "../../../../../utils/localStorage";
import { useQuickBooksInvoiceSync, quickBooksSyncTemplate } from "../../../../common/QuickBooksSyncColumn";

const ETIMS_BASE_URL =
  process.env.REACT_APP_INVOICES_SERVICE_URL || "https://inv.payserve.co.ke";

const MAIN_API_URL =
  process.env.REACT_APP_API_URL || "https://api.payserve.co.ke";

const etimsAxios = axios.create({
  baseURL: ETIMS_BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

// ─── tiny helpers ────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString("en-KE", { minimumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const fmtYM = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return new Date(y, +m - 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

// ─── generate quarter options ─────────────────────────────────────────────────
const buildQuarterOptions = () => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 8; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i * 3, 1);
    const y = d.getFullYear();
    const q = Math.floor(d.getMonth() / 3) + 1;
    const startMonth = (q - 1) * 3 + 1;
    const endMonth = startMonth + 2;
    opts.push({
      label: `Q${q} ${y}`,
      start: `${y}-${String(startMonth).padStart(2, "0")}`,
      end: `${y}-${String(endMonth).padStart(2, "0")}`,
    });
  }
  return opts;
};

// ─── ExportFilterPanel ────────────────────────────────────────────────────────
const ExportFilterPanel = ({ invoices, facilityId, availableYearMonths, onClose }) => {
  const toast = useRef(null);

  // Filter state
  const [filterMode, setFilterMode] = useState("month"); // month | quarter | range | all
  const [selectedMonth, setSelectedMonth] = useState(availableYearMonths[0] || "");
  const [selectedQuarter, setSelectedQuarter] = useState(buildQuarterOptions()[0]);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedLevyType, setSelectedLevyType] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");

  // Data for filter dropdowns
  const [levyTypes, setLevyTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [exportData, setExportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewed, setIsPreviewed] = useState(false);

  const quarterOptions = buildQuarterOptions();

  // Derive unique customers and units from already-loaded invoices for filter dropdowns
  useEffect(() => {
    const custMap = {};
    const unitMap = {};
    invoices.forEach((inv) => {
      const cid = inv.client?.clientId?.toString();
      if (cid && !custMap[cid]) {
        custMap[cid] = {
          id: cid,
          name: `${inv.client?.firstName || ""} ${inv.client?.lastName || ""}`.trim(),
        };
      }
      const uid = inv.unit?.id?.toString();
      if (uid && !unitMap[uid]) {
        unitMap[uid] = { id: uid, name: inv.unit?.name || uid };
      }
    });
    setCustomers(Object.values(custMap).sort((a, b) => a.name.localeCompare(b.name)));
    setUnits(Object.values(unitMap).sort((a, b) => a.name.localeCompare(b.name)));
  }, [invoices]);

  // Fetch levy types for the facility
  useEffect(() => {
    const fetchLevyTypes = async () => {
      try {
        // FIXED: Remove the extra $ sign from the URL
        const res = await makeRequest2(
          `/api/app/levy_management/get_levy_types/${facilityId}`,
          "GET"
        );
        if (res.success) {
          setLevyTypes(res.data?.levyTypes || res.data?.data || res.data || []);
        }
      } catch (e) {
        console.error("Failed to fetch levy types:", e.message);
      }
    };
    if (facilityId) fetchLevyTypes();
  }, [facilityId]);

  // Build query params from current filter state
  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (filterMode === "month" && selectedMonth) params.set("yearMonth", selectedMonth);
    if (filterMode === "quarter" && selectedQuarter) {
      params.set("startYearMonth", selectedQuarter.start);
      params.set("endYearMonth", selectedQuarter.end);
    }
    if (filterMode === "range") {
      if (rangeStart) params.set("startYearMonth", rangeStart);
      if (rangeEnd) params.set("endYearMonth", rangeEnd);
    }
    if (selectedStatus) params.set("status", selectedStatus);
    if (selectedLevyType) params.set("levyTypeId", selectedLevyType);
    if (selectedUnit) params.set("unitId", selectedUnit);
    if (selectedCustomer) params.set("customerId", selectedCustomer);
    return params.toString();
  };

  // Fetch export data from backend
  const fetchExportData = async () => {
    setIsLoading(true);
    try {
      const qs = buildQueryParams();
      const url = `/api/app/levy_management/invoices/export/${facilityId}${qs ? "?" + qs : ""}`;
      console.log("Fetching export data from:", url);

      const res = await makeRequest2(url, "GET");

      if (res.success) {
        // Handle both possible response structures
        const data = res.data?.data || res.data || [];
        setExportData(data);
        setIsPreviewed(true);
        if (data.length === 0) {
          toast.current?.show({
            severity: "warn",
            summary: "No Data",
            detail: res.message || "No invoices match the selected filters",
            life: 3000,
          });
        }
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: res.error || "Failed to fetch export data",
          life: 4000,
        });
      }
    } catch (e) {
      console.error("Export fetch error:", e);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: e.message,
        life: 4000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ── Export to Excel ──────────────────────────────────────────────────────
  const exportToExcel = () => {
    if (!exportData || exportData.length === 0) return;

    const rows = exportData.map((inv) => ({
      "Invoice #": inv.invoiceNumber,
      "Year Month": inv.yearMonth,
      "Issue Date": fmtDate(inv.issueDate),
      "Due Date": fmtDate(inv.dueDate),
      Customer: inv.customerName,
      Unit: inv.unitName,
      "Levy Type": inv.levyTypeName,
      "Levy Name": inv.levyName,
      Currency: inv.currency,
      "Sub Total": inv.subTotal,
      "Tax (VAT)": inv.taxAmount,
      "Balance B/F": inv.balanceBroughtForward,
      "Total Amount": inv.totalAmount,
      "Amount Paid": inv.amountPaid,
      Outstanding: inv.outstanding,
      Status: inv.status,
      "eTIMS Status": inv.etimsStatus,
      "eTIMS Receipt #": inv.etimsReceiptNo || "",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);

    // Auto-size columns
    const colWidths = Object.keys(rows[0] || {}).map((key) => ({
      wch: Math.max(key.length, 14),
    }));
    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Levy Invoices");

    const filename = `levy_invoices_${filterMode === "month" ? selectedMonth : filterMode}_${Date.now()}.xlsx`;
    XLSX.writeFile(wb, filename);
    toastify("Excel file downloaded", "success");
  };

  // ── Export to PDF ────────────────────────────────────────────────────────
  const exportToPDF = () => {
    if (!exportData || exportData.length === 0) return;

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    // Title
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Levy Invoices Report", 14, 15);

    // Subtitle / filter info
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    let filterLabel = "All Periods";
    if (filterMode === "month" && selectedMonth) filterLabel = fmtYM(selectedMonth);
    if (filterMode === "quarter" && selectedQuarter) filterLabel = selectedQuarter.label;
    if (filterMode === "range" && (rangeStart || rangeEnd))
      filterLabel = `${fmtYM(rangeStart) || "Start"} — ${fmtYM(rangeEnd) || "End"}`;
    doc.text(`Period: ${filterLabel}  |  Generated: ${new Date().toLocaleDateString()}  |  Total: ${exportData.length} invoices`, 14, 22);

    // Summary bar - using native reduce instead of R.reduce
    const totalAmt = exportData.reduce((s, i) => s + (i.totalAmount || 0), 0);
    const totalPaid = exportData.reduce((s, i) => s + (i.amountPaid || 0), 0);
    const totalOut = exportData.reduce((s, i) => s + (i.outstanding || 0), 0);

    doc.setFontSize(8);
    doc.text(
      `Total Billed: KES ${fmt(totalAmt)}   |   Collected: KES ${fmt(totalPaid)}   |   Outstanding: KES ${fmt(totalOut)}`,
      14,
      28
    );

    // Table
    autoTable(doc, {
      startY: 32,
      head: [
        [
          "Invoice #",
          "Month",
          "Customer",
          "Unit",
          "Levy Type",
          "Sub Total",
          "VAT",
          "B/F",
          "Total",
          "Paid",
          "Outstanding",
          "Status",
        ],
      ],
      body: exportData.map((inv) => [
        inv.invoiceNumber,
        inv.yearMonth,
        inv.customerName,
        inv.unitName,
        inv.levyTypeName,
        fmt(inv.subTotal),
        fmt(inv.taxAmount),
        fmt(inv.balanceBroughtForward),
        fmt(inv.totalAmount),
        fmt(inv.amountPaid),
        fmt(inv.outstanding),
        inv.status,
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
        9: { halign: "right" },
        10: { halign: "right" },
      },
    });

    // Footer page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() - 25, doc.internal.pageSize.getHeight() - 8);
    }

    const filename = `levy_invoices_${filterMode === "month" ? selectedMonth : filterMode}_${Date.now()}.pdf`;
    doc.save(filename);
    toastify("PDF downloaded", "success");
  };

  // ── Export to CSV ────────────────────────────────────────────────────────
  const exportToCSV = () => {
    if (!exportData || exportData.length === 0) return;

    const headers = [
      "Invoice #", "Year Month", "Issue Date", "Due Date",
      "Customer", "Unit", "Levy Type", "Levy Name",
      "Currency", "Sub Total", "Tax (VAT)", "Balance B/F",
      "Total Amount", "Amount Paid", "Outstanding", "Status",
      "eTIMS Status", "eTIMS Receipt #",
    ];

    const csvRows = [
      headers.join(","),
      ...exportData.map((inv) =>
        [
          inv.invoiceNumber,
          inv.yearMonth,
          fmtDate(inv.issueDate),
          fmtDate(inv.dueDate),
          `"${inv.customerName}"`,
          `"${inv.unitName}"`,
          `"${inv.levyTypeName}"`,
          `"${inv.levyName}"`,
          inv.currency,
          inv.subTotal,
          inv.taxAmount,
          inv.balanceBroughtForward,
          inv.totalAmount,
          inv.amountPaid,
          inv.outstanding,
          inv.status,
          inv.etimsStatus,
          inv.etimsReceiptNo || "",
        ].join(",")
      ),
    ];

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `levy_invoices_${filterMode === "month" ? selectedMonth : filterMode}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toastify("CSV downloaded", "success");
  };

  return (
    <>
      <Toast ref={toast} />
      <div style={{ minWidth: 0 }}>

        {/* ── Period filter mode tabs ─────────────────────────────────── */}
        <div className="mb-3">
          <label className="fw-bold d-block mb-2">Select Period</label>
          <div className="btn-group w-100" role="group">
            {[["month", "Single Month"], ["quarter", "Quarter"], ["range", "Date Range"], ["all", "All Time"]].map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                className={`btn btn-sm ${filterMode === mode ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => { setFilterMode(mode); setIsPreviewed(false); setExportData(null); }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Period selector ─────────────────────────────────────────── */}
        {filterMode === "month" && (
          <div className="mb-3">
            <select
              className="form-control"
              value={selectedMonth}
              onChange={(e) => { setSelectedMonth(e.target.value); setIsPreviewed(false); }}
            >
              <option value="">— All Months —</option>
              {availableYearMonths.map((ym) => (
                <option key={ym} value={ym}>{fmtYM(ym)}</option>
              ))}
            </select>
          </div>
        )}

        {filterMode === "quarter" && (
          <div className="mb-3">
            <select
              className="form-control"
              value={JSON.stringify(selectedQuarter)}
              onChange={(e) => { setSelectedQuarter(JSON.parse(e.target.value)); setIsPreviewed(false); }}
            >
              {quarterOptions.map((q) => (
                <option key={q.label} value={JSON.stringify(q)}>{q.label}</option>
              ))}
            </select>
          </div>
        )}

        {filterMode === "range" && (
          <div className="row g-2 mb-3">
            <div className="col-6">
              <label className="small text-muted">From (YYYY-MM)</label>
              <input
                type="month"
                className="form-control"
                value={rangeStart}
                onChange={(e) => { setRangeStart(e.target.value); setIsPreviewed(false); }}
              />
            </div>
            <div className="col-6">
              <label className="small text-muted">To (YYYY-MM)</label>
              <input
                type="month"
                className="form-control"
                value={rangeEnd}
                onChange={(e) => { setRangeEnd(e.target.value); setIsPreviewed(false); }}
              />
            </div>
          </div>
        )}

        {/* ── Additional filters ──────────────────────────────────────── */}
        <div className="border-top pt-3 mb-3">
          <label className="fw-bold d-block mb-2 text-muted small">Additional Filters (optional)</label>
          <div className="row g-2">
            <div className="col-6">
              <label className="small text-muted">Status</label>
              <select
                className="form-control form-control-sm"
                value={selectedStatus}
                onChange={(e) => { setSelectedStatus(e.target.value); setIsPreviewed(false); }}
              >
                <option value="">All Statuses</option>
                {["Unpaid", "Paid", "Overdue", "Void"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div className="col-6">
              <label className="small text-muted">Levy Type</label>
              <select
                className="form-control form-control-sm"
                value={selectedLevyType}
                onChange={(e) => { setSelectedLevyType(e.target.value); setIsPreviewed(false); }}
              >
                <option value="">All Levy Types</option>
                {levyTypes.map((lt) => (
                  <option key={lt._id} value={lt._id}>{lt.name}</option>
                ))}
              </select>
            </div>

            <div className="col-6">
              <label className="small text-muted">Customer</label>
              <select
                className="form-control form-control-sm"
                value={selectedCustomer}
                onChange={(e) => { setSelectedCustomer(e.target.value); setIsPreviewed(false); }}
              >
                <option value="">All Customers</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="col-6">
              <label className="small text-muted">Unit</label>
              <select
                className="form-control form-control-sm"
                value={selectedUnit}
                onChange={(e) => { setSelectedUnit(e.target.value); setIsPreviewed(false); }}
              >
                <option value="">All Units</option>
                {units.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ── Preview button ─────────────────────────────────────────── */}
        <Button
          label={isLoading ? "Loading..." : "Preview Data"}
          icon={isLoading ? "ti ti-loader" : "ti ti-eye"}
          className="btn btn-secondary w-100 mb-3"
          onClick={fetchExportData}
          loading={isLoading}
          disabled={isLoading}
        />

        {/* ── Preview summary ────────────────────────────────────────── */}
        {isPreviewed && exportData && (
          <div className="mb-3">
            <div
              className="border rounded p-3"
              style={{ background: exportData.length > 0 ? "#f0f9ff" : "#fff8f0" }}
            >
              {exportData.length === 0 ? (
                <div className="text-center text-muted py-2">
                  <i className="ti ti-inbox me-2" />
                  No invoices match the selected filters
                </div>
              ) : (
                <>
                  <div className="row g-2 text-center mb-2">
                    <div className="col-4">
                      <div className="fw-bold text-primary fs-5">{exportData.length}</div>
                      <small className="text-muted">Invoices</small>
                    </div>
                    <div className="col-4">
                      <div className="fw-bold text-success" style={{ fontSize: "0.95rem" }}>
                        KES {fmt(exportData.reduce((s, i) => s + (i.totalAmount || 0), 0))}
                      </div>
                      <small className="text-muted">Total Billed</small>
                    </div>
                    <div className="col-4">
                      <div className="fw-bold text-warning" style={{ fontSize: "0.95rem" }}>
                        KES {fmt(exportData.reduce((s, i) => s + (i.outstanding || 0), 0))}
                      </div>
                      <small className="text-muted">Outstanding</small>
                    </div>
                  </div>

                  {/* Mini preview table — first 5 rows */}
                  <div style={{ overflowX: "auto", fontSize: "0.78rem" }}>
                    <table className="table table-sm table-bordered mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Invoice #</th>
                          <th>Customer</th>
                          <th>Unit</th>
                          <th>Levy Type</th>
                          <th>Month</th>
                          <th className="text-end">Total</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exportData.slice(0, 5).map((inv) => (
                          <tr key={inv.invoiceId}>
                            <td>{inv.invoiceNumber}</td>
                            <td>{inv.customerName}</td>
                            <td>{inv.unitName}</td>
                            <td>{inv.levyTypeName}</td>
                            <td>{inv.yearMonth}</td>
                            <td className="text-end">KES {fmt(inv.totalAmount)}</td>
                            <td>
                              <span
                                style={{
                                  fontSize: "0.72rem",
                                  padding: "2px 6px",
                                  borderRadius: "10px",
                                  background: inv.status === "Paid" ? "#e8f5e9" : inv.status === "Overdue" ? "#ffebee" : "#fff3e0",
                                  color: inv.status === "Paid" ? "#4caf50" : inv.status === "Overdue" ? "#f44336" : "#ff9800",
                                }}
                              >
                                {inv.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {exportData.length > 5 && (
                      <p className="text-muted text-end mb-0 mt-1" style={{ fontSize: "0.75rem" }}>
                        … and {exportData.length - 5} more rows
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Export buttons ─────────────────────────────────────────── */}
        {isPreviewed && exportData && exportData.length > 0 && (
          <div className="row g-2">
            <div className="col-4">
              <button
                className="btn btn-success w-100"
                onClick={exportToExcel}
                title="Download Excel (.xlsx)"
              >
                <i className="ti ti-file-spreadsheet me-1" />
                Excel
              </button>
            </div>
            <div className="col-4">
              <button
                className="btn btn-danger w-100"
                onClick={exportToPDF}
                title="Download PDF"
              >
                <i className="ti ti-file-type-pdf me-1" />
                PDF
              </button>
            </div>
            <div className="col-4">
              <button
                className="btn btn-secondary w-100"
                onClick={exportToCSV}
                title="Download CSV"
              >
                <i className="ti ti-file-text me-1" />
                CSV
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// ─── UnitBalancesPanel ─────────────────────────────────────────────────────
const UnitBalancesPanel = ({ facilityId }) => {
  const toast = useRef(null);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [showOnlyOutstanding, setShowOnlyOutstanding] = useState(false);

  const fetchBalances = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const res = await makeRequest2(`${getFacilityInvoiceBalances}/${facilityId}`, "GET");
      if (res.success) {
        setBalances(res.data?.data || res.data || []);
      } else {
        toast.current?.show({ severity: "error", summary: "Error", detail: res.error || "Failed to fetch unit balances", life: 4000 });
      }
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "Error", detail: e.message, life: 4000 });
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  const filtered = balances.filter((b) => {
    const matchesSearch =
      b.unitName?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
      b.customerName?.toLowerCase().includes(searchKeyword.toLowerCase());
    const matchesOutstanding = !showOnlyOutstanding || b.balance > 0;
    return matchesSearch && matchesOutstanding;
  });

  const totals = filtered.reduce(
    (acc, b) => ({
      billed: acc.billed + (b.totalBilled || 0),
      paid: acc.paid + (b.totalPaid || 0),
      balance: acc.balance + (b.balance || 0),
    }),
    { billed: 0, paid: 0, balance: 0 }
  );

  const balanceBodyTemplate = (rowData) => {
    const isSettled = rowData.balance <= 0;
    return (
      <span
        style={{
          fontWeight: "bold",
          color: isSettled ? "#4caf50" : "#f44336",
        }}
      >
        {rowData.currencyCode || "KES"} {fmt(rowData.balance)}
      </span>
    );
  };

  const exportBalancesToExcel = () => {
    if (!filtered.length) return;
    const rows = filtered.map((b) => ({
      Unit: b.unitName,
      Customer: b.customerName,
      Currency: b.currencyCode,
      "Total Billed": b.totalBilled,
      "Total Paid": b.totalPaid,
      Balance: b.balance,
      Invoices: b.invoiceCount,
      "First Invoice": fmtDate(b.firstInvoiceDate),
      "Last Invoice": fmtDate(b.lastInvoiceDate),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0] || {}).map((k) => ({ wch: Math.max(k.length, 14) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Unit Balances");
    XLSX.writeFile(wb, `unit_balances_${Date.now()}.xlsx`);
    toastify("Excel file downloaded", "success");
  };

  return (
    <>
      <Toast ref={toast} />
      <div style={{ minWidth: 0 }}>
        {/* Summary row */}
        <div className="row g-2 text-center mb-3">
          <div className="col-4">
            <div className="border rounded p-3" style={{ background: "#f0f9ff" }}>
              <div className="fw-bold text-primary fs-5">{filtered.length}</div>
              <small className="text-muted">Units</small>
            </div>
          </div>
          <div className="col-4">
            <div className="border rounded p-3" style={{ background: "#f0f9ff" }}>
              <div className="fw-bold text-success" style={{ fontSize: "0.95rem" }}>
                KES {fmt(totals.paid)}
              </div>
              <small className="text-muted">Total Paid</small>
            </div>
          </div>
          <div className="col-4">
            <div className="border rounded p-3" style={{ background: totals.balance > 0 ? "#fff8f0" : "#f0f9ff" }}>
              <div className="fw-bold" style={{ fontSize: "0.95rem", color: totals.balance > 0 ? "#f44336" : "#4caf50" }}>
                KES {fmt(totals.balance)}
              </div>
              <small className="text-muted">Outstanding</small>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row g-2 align-items-center mb-3">
          <div className="col-8">
            <div className="p-input-icon-left w-100">
              <i className="pi pi-search" />
              <InputText
                placeholder="Search unit or customer..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="w-100"
                style={{ paddingLeft: "2.5rem" }}
              />
            </div>
          </div>
          <div className="col-4 d-flex align-items-center gap-2">
            <input
              type="checkbox"
              id="outstandingOnly"
              checked={showOnlyOutstanding}
              onChange={(e) => setShowOnlyOutstanding(e.target.checked)}
            />
            <label htmlFor="outstandingOnly" className="mb-0 small">Outstanding only</label>
          </div>
        </div>

        <DataTable
          value={filtered}
          loading={loading}
          paginator
          rows={10}
          emptyMessage="No unit balances found"
          className="p-datatable-sm"
          stripedRows
          sortField="balance"
          sortOrder={-1}
          responsiveLayout="scroll"
        >
          <Column field="unitName" header="Unit" sortable style={{ minWidth: "140px" }} />
          <Column field="customerName" header="Customer" sortable style={{ minWidth: "180px" }} />
          <Column field="totalBilled" header="Billed" sortable body={(r) => `${r.currencyCode || "KES"} ${fmt(r.totalBilled)}`} style={{ minWidth: "130px" }} />
          <Column field="totalPaid" header="Paid" sortable body={(r) => `${r.currencyCode || "KES"} ${fmt(r.totalPaid)}`} style={{ minWidth: "130px" }} />
          <Column field="balance" header="Balance" sortable body={balanceBodyTemplate} style={{ minWidth: "140px" }} />
          <Column field="invoiceCount" header="Invoices" sortable style={{ minWidth: "90px" }} />
          <Column field="lastInvoiceDate" header="Last Invoice" body={(r) => fmtDate(r.lastInvoiceDate)} sortable style={{ minWidth: "120px" }} />
        </DataTable>

        <div className="d-flex justify-content-end mt-3">
          <button className="btn btn-success" onClick={exportBalancesToExcel} disabled={!filtered.length}>
            <i className="ti ti-file-spreadsheet me-1" />
            Export Excel
          </button>
        </div>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT — InvoiceManagement (your existing component, export panel added)
// ─────────────────────────────────────────────────────────────────────────────
const InvoiceManagement = () => {
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const toast = useRef(null);

  const [selectedYearMonth, setSelectedYearMonth] = useState("all");
  const [availableYearMonths, setAvailableYearMonths] = useState([]);

  const [showVoidModal, setShowVoidModal] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [isVoiding, setIsVoiding] = useState(false);

  const [isSyncingInvoice, setIsSyncingInvoice] = useState(null);
  const [isSyncingFacility, setIsSyncingFacility] = useState(false);

  const [showEtimsSyncModal, setShowEtimsSyncModal] = useState(false);
  const [etimsSyncYearMonth, setEtimsSyncYearMonth] = useState("");
  const [etimsSyncPreview, setEtimsSyncPreview] = useState(null);

  // ── NEW: export panel state ───────────────────────────────────────────────
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [showBalancesPanel, setShowBalancesPanel] = useState(false);
  const [facilityId, setFacilityId] = useState(null);
  const { enabled: qbEnabled, statuses: qbStatuses } = useQuickBooksInvoiceSync(facilityId, invoices);
  // ─────────────────────────────────────────────────────────────────────────

  const [currentUser, setCurrentUser] = useState(null);
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");
  const [userId, setUserId] = useState(null);

  const [statistics, setStatistics] = useState({
    totalUnits: 0,
    unitsWithActiveLevy: 0,
    unitsWithoutLevy: 0,
    totalLevyAmount: 0,
    collectedAmount: 0,
    outstandingAmount: 0,
  });

  useEffect(() => {
    const init = async () => {
      const fid = await getItem("selectedFacilityId");
      setFacilityId(fid);

      try {
        const currentUserValue = await getItem("APPUSER");
        let parsedUser =
          typeof currentUserValue === "string"
            ? JSON.parse(currentUserValue)
            : currentUserValue;
        if (parsedUser?.user) {
          const u = parsedUser.user;
          setCurrentUser(u);
          setFullName(u.fullName || "");
          setRole(u.role || "");
          setUserId(u._id || u.id || u.userId || null);
        }
      } catch (err) {
        console.error("Error getting current user:", err);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (invoices.length > 0) {
      const yms = [...new Set(invoices.map((inv) => inv.yearMonth).filter(Boolean))];
      setAvailableYearMonths(yms.sort((a, b) => new Date(b + "-01") - new Date(a + "-01")));
    }
  }, [invoices]);

  const fetchInvoices = async () => {
    setLoading(true);
    const fid = facilityId || (await getItem("selectedFacilityId"));
    try {
      const response = await makeRequest2(`${getFacilityInvoices}/${fid}`, "GET");
      console.log("API Response:", response);
      if (response.success) {
        const mapped = response.data.data.map((invoice) => ({
          ...invoice,
          fullName: invoice.customerInfo?.fullName || "",
        }));
        setInvoices(mapped);
        await calculateStatistics(mapped, fid);
      } else {
        toast.current.show({ severity: "error", summary: "Error", detail: response.error || "Failed to fetch invoices" });
      }
    } catch (error) {
      toast.current.show({ severity: "error", summary: "Error", detail: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (facilityId) fetchInvoices();
  }, [facilityId]);

  const notificationBodyTemplate = (rowData) => {
    const n = rowData.notificationsSent || {};
    if (n.sms && n.email) return <span>Email & SMS</span>;
    if (n.email) return <span>Email</span>;
    if (n.sms) return <span>SMS</span>;
    return <span className="text-muted">None</span>;
  };

  const syncBodyTemplate = (rowData) => {
    const synced = rowData?.digitax?.synced;

    return (
      <Tag
        value={synced ? "Synced" : "Not Synced"}
        severity={synced ? "success" : "danger"}
      />
    );
  };

  const creditNoteSyncBodyTemplate = (rowData) => {
    const synced = rowData?.digitax?.creditNote?.synced;

    return (
      <Tag
        value={synced ? "Synced" : "Not Synced"}
        severity={synced ? "success" : "warning"}
      />
    );
  };

  const calculateStatistics = async (invoiceData, fid) => {
    try {
      const unitsResponse = await makeRequest2(`${getFacilityUnits}/${fid}`, "GET");
      const contractsResponse = await makeRequest2(`${getContracts}/${fid}`, "GET");
      if (unitsResponse.success) {
        const totalUnits = unitsResponse.data.units?.length || 0;
        let activeContracts = [];
        if (contractsResponse.success) {
          const cd = contractsResponse.data?.contracts || contractsResponse.data || [];
          activeContracts = cd.filter((c) => c.status === "Active" && new Date(c.endDate) > new Date());
        }
        const unitsWithLevy = new Set();
        activeContracts.forEach((c) => { if (c.unitId) unitsWithLevy.add(c.unitId); });
        const levyInvoices = invoiceData.filter((i) => i.status !== "Void");
        const totalLevyAmount = levyInvoices.reduce((s, i) => s + (i.totalAmount || 0), 0);
        const collectedAmount = levyInvoices.reduce((s, i) => s + (i.amountPaid || 0), 0);
        setStatistics({
          totalUnits,
          unitsWithActiveLevy: unitsWithLevy.size,
          unitsWithoutLevy: totalUnits - unitsWithLevy.size,
          totalLevyAmount,
          collectedAmount,
          outstandingAmount: totalLevyAmount - collectedAmount,
        });
      }
    } catch (error) {
      console.error("Error calculating statistics:", error);
    }
  };

  const openVoidModal = (invoiceId) => { setSelectedInvoiceId(invoiceId); setVoidReason(""); setShowVoidModal(true); };
  const closeVoidModal = () => { setShowVoidModal(false); setSelectedInvoiceId(null); setVoidReason(""); };

  const handleVoidInvoice = async () => {
    const fid = facilityId || (await getItem("selectedFacilityId"));
    if (!voidReason.trim()) {
      toast.current.show({ severity: "warn", summary: "Validation Error", detail: "Please provide a reason for voiding this invoice", life: 3000 });
      return;
    }
    setIsVoiding(true);
    try {
      const payload = {
        reason: voidReason.trim(),
        userId: userId || currentUser?._id || "unknown",
        userName: fullName,
        userRole: role || "Unknown",
      };
      const response = await makeRequest2(`${voidInvoice}/${fid}/${selectedInvoiceId}`, "POST", payload);
      if (response.data || response.success) {
        toastify("Invoice successfully voided", "success");
        closeVoidModal();
        fetchInvoices();
      } else {
        toast.current.show({ severity: "error", summary: "Error", detail: response.error || "Failed to void invoice", life: 3000 });
      }
    } catch (error) {
      toast.current.show({ severity: "error", summary: "Error", detail: error.message, life: 3000 });
    } finally {
      setIsVoiding(false);
    }
  };

  // ── eTIMS handlers (unchanged from your working version) ─────────────────
  const handleSyncInvoiceToEtims = async (invoiceId) => {
    const fid = facilityId || (await getItem("selectedFacilityId"));
    setIsSyncingInvoice(invoiceId);
    try {
      const { data } = await etimsAxios.post(`/v1/api/etims/sync/${invoiceId}`, { facilityId: fid });
      if (data?.success) {
        toastify(data.message || "Invoice successfully synced to eTIMS", "success");
        fetchInvoices();
      } else {
        toast.current.show({ severity: "error", summary: "eTIMS Sync Failed", detail: data?.message || "Failed to sync invoice to eTIMS", life: 4000 });
      }
    } catch (error) {
      toast.current.show({ severity: "error", summary: "eTIMS Sync Error", detail: error.response?.data?.message || error.message, life: 4000 });
    } finally {
      setIsSyncingInvoice(null);
    }
  };

  const handleSyncFacilityInvoicesToEtims = async () => {
    if (!etimsSyncYearMonth) {
      toast.current.show({ severity: "warn", summary: "Validation", detail: "Please select a month to sync.", life: 3000 });
      return;
    }
    const fid = facilityId || (await getItem("selectedFacilityId"));
    setIsSyncingFacility(true);
    try {
      const { data } = await etimsAxios.post("/v1/api/etims/sync-yearmonth", {
        facilityId: fid,
        yearMonth: etimsSyncYearMonth,
        force: false,
      });
      if (data?.success) {
        const { synced = 0, failed = 0, skipped = 0, total = 0 } = data;
        toastify(`Sync complete for ${fmtYM(etimsSyncYearMonth)}: ${synced}/${total} synced, ${skipped} skipped, ${failed} failed.`, failed > 0 ? "warn" : "success");
        closeEtimsSyncModal();
        fetchInvoices();
      } else {
        toast.current.show({ severity: "error", summary: "eTIMS Sync Failed", detail: data?.message || "Failed to sync facility invoices to eTIMS", life: 4000 });
      }
    } catch (error) {
      toast.current.show({ severity: "error", summary: "eTIMS Sync Error", detail: error.response?.data?.message || error.message, life: 4000 });
    } finally {
      setIsSyncingFacility(false);
    }
  };

  const openEtimsSyncModal = () => {
    const defaultMonth = selectedYearMonth !== "all" ? selectedYearMonth : (availableYearMonths[0] || "");
    setEtimsSyncYearMonth(defaultMonth);
    computeEtimsSyncPreview(defaultMonth);
    setShowEtimsSyncModal(true);
  };
  const closeEtimsSyncModal = () => { setShowEtimsSyncModal(false); setEtimsSyncYearMonth(""); setEtimsSyncPreview(null); };
  const computeEtimsSyncPreview = (ym) => {
    if (!ym) { setEtimsSyncPreview(null); return; }
    const monthInvoices = invoices.filter((inv) => inv.yearMonth === ym && inv.status !== "Void");
    const alreadySynced = monthInvoices.filter((inv) => inv.txsync?.status === "synced").length;
    setEtimsSyncPreview({ total: monthInvoices.length, alreadySynced, toSync: monthInvoices.length - alreadySynced });
  };
  const handleEtimsSyncYearMonthChange = (value) => { setEtimsSyncYearMonth(value); computeEtimsSyncPreview(value); };
  // ─────────────────────────────────────────────────────────────────────────

  const statusTemplate = (rowData) => {
    const map = {
      Paid: { color: "#4caf50", bg: "#e8f5e9" },
      Unpaid: { color: "#ff9800", bg: "#fff3e0" },
      Overdue: { color: "#f44336", bg: "#ffebee" },
      Void: { color: "#9e9e9e", bg: "#f5f5f5" },
    };
    const s = map[rowData.status] || { color: "#2196f3", bg: "#e3f2fd" };
    return <span style={{ fontWeight: "bold", color: s.color, padding: "5px 10px", borderRadius: "15px", backgroundColor: s.bg }}>{rowData.status}</span>;
  };

  const etimsSyncTemplate = (rowData) => {
    const syncStatus = rowData.txsync?.status || "not-applicable";
    const cfg = {
      synced: { color: "#4caf50", bg: "#e8f5e9", icon: "ti ti-circle-check", label: "Synced" },
      pending: { color: "#2196f3", bg: "#e3f2fd", icon: "ti ti-clock", label: "Pending" },
      failed: { color: "#f44336", bg: "#ffebee", icon: "ti ti-alert-circle", label: "Failed" },
      "not-applicable": { color: "#9e9e9e", bg: "#f5f5f5", icon: "ti ti-minus", label: "N/A" },
    }[syncStatus] || { color: "#9e9e9e", bg: "#f5f5f5", icon: "ti ti-minus", label: "N/A" };
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontWeight: "600", color: cfg.color, padding: "4px 10px", borderRadius: "15px", backgroundColor: cfg.bg, fontSize: "0.82rem" }}>
        <i className={cfg.icon} />
        {cfg.label}
      </span>
    );
  };

  const zohoSyncTemplate = (rowData) => {
    const syncStatus = rowData.zohoSync?.status || "not-applicable";
    const cfg = {
      synced: { color: "#4caf50", bg: "#e8f5e9", icon: "ti ti-circle-check", label: "Synced" },
      pending: { color: "#2196f3", bg: "#e3f2fd", icon: "ti ti-clock", label: "Pending" },
      failed: { color: "#f44336", bg: "#ffebee", icon: "ti ti-alert-circle", label: "Failed" },
      "not-applicable": { color: "#9e9e9e", bg: "#f5f5f5", icon: "ti ti-minus", label: "N/A" },
    }[syncStatus] || { color: "#9e9e9e", bg: "#f5f5f5", icon: "ti ti-minus", label: "N/A" };
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontWeight: "600", color: cfg.color, padding: "4px 10px", borderRadius: "15px", backgroundColor: cfg.bg, fontSize: "0.82rem" }}>
        <i className={cfg.icon} />
        {cfg.label}
      </span>
    );
  };

  const actionTemplate = (rowData) => {
    const isSynced = rowData.txsync?.status === "synced";
    const isSyncingThis = isSyncingInvoice === rowData._id;
    return (
      <div className="flex gap-2">
        <Button icon="ti ti-eye" className="p-button-rounded p-button-success" onClick={(e) => { e.stopPropagation(); navigate(`/facility/levy_management/invoice/${rowData._id}`); }} tooltip="View Details" />
        {rowData.status !== "Void" && rowData.status !== "Paid" && (
          <Button icon="ti ti-trash" className="p-button-rounded p-button-warning" tooltip="Void Invoice" onClick={(e) => { e.stopPropagation(); openVoidModal(rowData._id); }} />
        )}
        {!isSynced && rowData.txsync?.status !== "pending" && (
          <Button icon={isSyncingThis ? "ti ti-loader" : "ti ti-refresh"} className="p-button-rounded p-button-info" tooltip="Sync to eTIMS" loading={isSyncingThis} disabled={isSyncingThis} onClick={(e) => { e.stopPropagation(); handleSyncInvoiceToEtims(rowData._id); }} />
        )}
      </div>
    );
  };

  const filterInvoices = (list) =>
    list.filter((invoice) => {
      const matchesSearch =
        invoice.invoiceNumber?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        invoice.fullName?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        invoice.status?.toLowerCase().includes(searchKeyword.toLowerCase());
      const matchesMonth = !selectedYearMonth || selectedYearMonth === "all" || invoice.yearMonth === selectedYearMonth;
      return matchesSearch && matchesMonth;
    });

  const allInvoices = filterInvoices(invoices);
  const unpaidInvoices = allInvoices.filter((i) => i.status === "Unpaid");
  const paidInvoices = allInvoices.filter((i) => i.status === "Paid");
  const overdueInvoices = allInvoices.filter((i) => i.status === "Overdue");
  const voidInvoices = allInvoices.filter((i) => i.status === "Void");
  const getInvoicesByTab = (idx) => [allInvoices, unpaidInvoices, paidInvoices, overdueInvoices, voidInvoices][idx] || allInvoices;

  const header = (
    <div className="mb-4 p-3 border rounded bg-white">
      <div className="row g-3 align-items-center">
        <div className="col-md-4">
          <div className="p-input-icon-left w-100">
            <i className="pi pi-search" />
            <InputText placeholder="Search invoices..." value={searchKeyword} onChange={(e) => setSearchKeyword(e.target.value)} className="w-100" style={{ height: "40px", paddingLeft: "2.5rem" }} />
          </div>
        </div>
        <div className="col-md-3">
          <select className="form-control" value={selectedYearMonth} onChange={(e) => setSelectedYearMonth(e.target.value)}>
            <option value="all">All Months</option>
            {availableYearMonths.map((ym) => <option key={ym} value={ym}>{fmtYM(ym)}</option>)}
          </select>
        </div>
        <div className="col-md-2">
          <Button label="Clear" className="p-button-text btn btn-secondary" type="button" onClick={() => { setSelectedYearMonth("all"); setSearchKeyword(""); }} />
        </div>
      </div>
    </div>
  );

  const voidModalFooter = (
    <div>
      <Button label="Cancel" onClick={closeVoidModal} className="p-button-text btn btn-secondary me-2" disabled={isVoiding} />
      <Button label={isVoiding ? "Voiding..." : "Void Invoice"} onClick={handleVoidInvoice} className="p-button-text btn btn-danger" disabled={isVoiding || !voidReason.trim()} loading={isVoiding} />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />

      {/* Void Modal */}
      <Dialog header="Void Invoice" visible={showVoidModal} style={{ width: "500px" }} onHide={closeVoidModal} footer={voidModalFooter} modal draggable={false}>
        <div className="p-fluid">
          <div className="mb-3">
            <p className="mb-2"><strong>Warning:</strong> You are about to void this invoice. This action cannot be undone.</p>
            <p className="text-muted mb-3">Voided by: <strong>{fullName}</strong> {role && `(${role})`}</p>
          </div>
          <div className="field">
            <label htmlFor="voidReason" className="font-bold">Reason for Voiding <span className="text-danger">*</span></label>
            <InputTextarea id="voidReason" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} rows={5} placeholder="Please provide a detailed reason..." className="w-100" disabled={isVoiding} autoFocus />
            {!voidReason.trim() && <small className="text-danger">This field is required</small>}
          </div>
        </div>
      </Dialog>

      {/* eTIMS Sync Modal */}
      <Dialog
        header={<div className="d-flex align-items-center gap-2"><i className="ti ti-refresh text-primary" style={{ fontSize: "1.2rem" }} /><span>Sync Facility Invoices to eTIMS</span></div>}
        visible={showEtimsSyncModal}
        style={{ width: "480px" }}
        onHide={closeEtimsSyncModal}
        modal
        draggable={false}
        footer={
          <div>
            <Button label="Cancel" onClick={closeEtimsSyncModal} className="p-button-text btn btn-secondary me-2" disabled={isSyncingFacility} />
            <Button icon={isSyncingFacility ? "ti ti-loader" : "ti ti-refresh"} label={isSyncingFacility ? "Syncing..." : "Sync Now"} onClick={handleSyncFacilityInvoicesToEtims} className="p-button btn btn-primary" loading={isSyncingFacility} disabled={isSyncingFacility || !etimsSyncYearMonth || etimsSyncPreview?.toSync === 0} />
          </div>
        }
      >
        <div className="p-fluid">
          <div className="alert alert-info d-flex align-items-start gap-2 mb-4" style={{ borderRadius: "8px", fontSize: "0.88rem" }}>
            <i className="ti ti-info-circle mt-1" style={{ flexShrink: 0 }} />
            <span>Select a billing month to submit all unsynced invoices to KRA eTIMS. Only invoices with a sync status other than <strong>Synced</strong> will be processed.</span>
          </div>
          <div className="field mb-3">
            <label className="font-bold mb-2 d-block">Billing Month <span className="text-danger">*</span></label>
            <select className="form-control" value={etimsSyncYearMonth} onChange={(e) => handleEtimsSyncYearMonthChange(e.target.value)} disabled={isSyncingFacility}>
              <option value="">— Select a month —</option>
              {availableYearMonths.map((ym) => <option key={ym} value={ym}>{fmtYM(ym)}</option>)}
            </select>
          </div>
          {etimsSyncPreview && etimsSyncYearMonth && (
            <div className="mt-3">
              <div className="row g-2">
                {[["Total Invoices", etimsSyncPreview.total, "#f8f9fa", "text-primary"], ["Already Synced", etimsSyncPreview.alreadySynced, "#e8f5e9", "#4caf50"], ["To Be Synced", etimsSyncPreview.toSync, etimsSyncPreview.toSync > 0 ? "#fff3e0" : "#f5f5f5", etimsSyncPreview.toSync > 0 ? "#ff9800" : "#9e9e9e"]].map(([label, val, bg, color]) => (
                  <div key={label} className="col-4">
                    <div className="text-center p-3 border rounded" style={{ background: bg }}>
                      <div className="fw-bold fs-5" style={{ color }}>{val}</div>
                      <small className="text-muted">{label}</small>
                    </div>
                  </div>
                ))}
              </div>
              {etimsSyncPreview.toSync === 0 && (
                <div className="alert alert-success mt-3 d-flex align-items-center gap-2" style={{ borderRadius: "8px", fontSize: "0.88rem" }}>
                  <i className="ti ti-circle-check" /> All invoices for {fmtYM(etimsSyncYearMonth)} are already synced.
                </div>
              )}
              {etimsSyncPreview.toSync > 0 && (
                <div className="alert alert-warning mt-3 d-flex align-items-center gap-2" style={{ borderRadius: "8px", fontSize: "0.88rem" }}>
                  <i className="ti ti-alert-triangle" />
                  <span><strong>{etimsSyncPreview.toSync}</strong> invoice{etimsSyncPreview.toSync !== 1 ? "s" : ""} will be submitted to eTIMS for {fmtYM(etimsSyncYearMonth)}.</span>
                </div>
              )}
            </div>
          )}
        </div>
      </Dialog>

      {/* ── NEW: Export Panel Modal ───────────────────────────────────────── */}
      <Dialog
        header={
          <div className="d-flex align-items-center gap-2">
            <i className="ti ti-download text-success" style={{ fontSize: "1.2rem" }} />
            <span>Export Levy Invoices</span>
          </div>
        }
        visible={showExportPanel}
        style={{ width: "580px" }}
        onHide={() => setShowExportPanel(false)}
        modal
        draggable={false}
      >
        {facilityId && (
          <ExportFilterPanel
            invoices={invoices}
            facilityId={facilityId}
            availableYearMonths={availableYearMonths}
            onClose={() => setShowExportPanel(false)}
          />
        )}
      </Dialog>


      <Dialog
        header={
          <div className="d-flex align-items-center gap-2">
            <i className="ti ti-report-money text-primary" style={{ fontSize: "1.2rem" }} />
            <span>Unit Balances</span>
          </div>
        }
        visible={showBalancesPanel}
        style={{ width: "780px" }}
        onHide={() => setShowBalancesPanel(false)}
        modal
        draggable={false}
      >
        {facilityId && <UnitBalancesPanel facilityId={facilityId} />}
      </Dialog>

      {/* Statistics Cards */}
      <div className="row mb-4">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="row">
                {[
                  ["ti-building", "text-primary", statistics.totalUnits, "Total Units", "h5"],
                  ["ti-circle-check", "text-success", statistics.unitsWithActiveLevy, "Units with Active Levy", "h5"],
                  ["ti-circle-x", "text-danger", statistics.unitsWithoutLevy, "Units without Levy", "h5"],
                  ["ti-cash", "text-info", `KES ${fmt(statistics.totalLevyAmount)}`, "Total Levy Amount", "h6"],
                  ["ti-cash", "text-success", `KES ${fmt(statistics.collectedAmount)}`, "Collected Amount", "h6"],
                  ["ti-alert-triangle", "text-warning", `KES ${fmt(statistics.outstandingAmount)}`, "Outstanding Amount", "h6"],
                ].map(([icon, color, val, label, tag], i) => (
                  <div key={i} className="col-md-2">
                    <div className="text-center p-3 border rounded bg-light">
                      <i className={`ti ${icon} ${color} f-24 mb-2`}></i>
                      {React.createElement(tag, { className: `mb-0 ${color}` }, val)}
                      <small className="text-muted">{label}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs + Action Buttons */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <ul className="nav nav-tabs invoice-tab border-bottom" id="invoiceTab" role="tablist">
          {[["All", allInvoices, "bg-light-primary", 0], ["Unpaid", unpaidInvoices, "bg-light-warning", 1], ["Paid", paidInvoices, "bg-light-success", 2], ["Overdue", overdueInvoices, "bg-light-danger", 3], ["Void", voidInvoices, "bg-light-secondary", 4]].map(([label, data, cls, idx]) => (
            <li key={idx} className="nav-item" role="presentation">
              <button className={`nav-link ${activeTabIndex === idx ? "active" : ""}`} onClick={() => setActiveTabIndex(idx)} type="button">
                <span className="d-flex align-items-center gap-2">{label} <span className={`avtar rounded-circle ${cls}`}>{data.length}</span></span>
              </button>
            </li>
          ))}
        </ul>

        {/* Action buttons row */}
        <div className="d-flex gap-2">
          {/* ── NEW: Export button ── */}
          <Button
            icon="ti ti-download"
            label="Export"
            className="p-button btn btn-success"
            onClick={() => setShowExportPanel(true)}
            style={{ whiteSpace: "nowrap" }}
          />
          <Button
            icon="ti ti-report-money"
            label="Unit Balances"
            className="p-button btn btn-info"
            onClick={() => setShowBalancesPanel(true)}
            style={{ whiteSpace: "nowrap" }}
          />
          <Button
            icon="ti ti-refresh"
            label="Sync to eTIMS"
            className="p-button btn btn-primary"
            onClick={openEtimsSyncModal}
            style={{ whiteSpace: "nowrap" }}
          />
        </div>
      </div>

      {/* Invoice Table */}
      <DataTable
        value={getInvoicesByTab(activeTabIndex)}
        header={header}
        loading={loading}
        paginator
        rows={10}
        emptyMessage="No levy invoices found"
        className="p-datatable-sm"
        stripedRows
        selectionMode="single"
        sortField="issueDate"
        sortOrder={-1}
        responsiveLayout="scroll"
      >
        <Column field="invoiceNumber" header="Invoice #" style={{ minWidth: "150px" }} />
        <Column field="fullName" header="Customer" style={{ minWidth: "200px" }} />
        <Column field="unit.name" header="Unit" style={{ minWidth: "150px" }} />
        <Column field="totalAmount" header="Amount" style={{ minWidth: "120px" }} body={(r) => `${r.currency?.code || "KES"} ${r.totalAmount.toLocaleString()}`} />
        <Column field="issueDate" header="Issue Date" body={(r) => fmtDate(r.issueDate)} style={{ minWidth: "120px" }} />
        <Column field="dueDate" header="Due Date" body={(r) => fmtDate(r.dueDate)} style={{ minWidth: "120px" }} />
        <Column field="yearMonth" header="Month" sortable />
        <Column header="Notification Sent" body={notificationBodyTemplate} style={{ width: "120px" }} />
        <Column field="status" header="Status" body={statusTemplate} style={{ minWidth: "120px" }} />
        <Column header="DigiTax Sync" body={syncBodyTemplate} style={{ minWidth: "140px" }} />
        <Column header="Credit Note" body={creditNoteSyncBodyTemplate} style={{ minWidth: "140px" }} />
        <Column header="eTIMS Sync" body={etimsSyncTemplate} style={{ minWidth: "130px" }} />
        <Column header="Zoho Sync" body={zohoSyncTemplate} style={{ minWidth: "120px" }} />
        {qbEnabled && <Column header="QuickBooks" body={quickBooksSyncTemplate(qbStatuses)} style={{ minWidth: "130px" }} />}
        <Column header="Actions" body={actionTemplate} style={{ minWidth: "130px" }} />
      </DataTable>
    </>
  );
};

export default InvoiceManagement;